import { useRuntimeConfig } from 'nuxt/app';

type ConsentCategories = {
    necessary?: boolean;
    functional?: boolean;
    analytics?: boolean;
    performance?: boolean;
    advertisement?: boolean;
};

const CONSENT_CATEGORIES = {
    necessary: 'Website Necessary Cookie Consent',
    functional: 'Website Functional Cookie Consent',
    analytics: 'Website Performance Cookie Consent',
    performance: 'Website Performance Cookies Consent',
    advertisement: 'Website Targeting Cookie Consent',
};

let cookieYesLoaded = false;
let cookieYesLoadingPromise: Promise<void> | null = null;
let listenersAttached = false;

export const useCookieYes = () => {
    const config = useRuntimeConfig();

    const sendConsentsToBloomreach = (categories: ConsentCategories): void => {
        if (!window.exponea || !categories) return;

        Object.entries(CONSENT_CATEGORIES).forEach(([key, label]) => {
            const isConsented = !!categories[key as keyof ConsentCategories];
            window.exponea.track('consent', {
                action: isConsented ? 'accept' : 'reject',
                category: label,
                valid_until: 'unlimited',
            });
        });
    }

    const checkConsent = (): boolean => {
        const getConsent = window.getCkyConsent;
        if (!getConsent) {
            console.error('[CookieYes] getConsent not defined');
            return false;
        }

        const consentData = getConsent();
        if (!consentData || !consentData.isUserActionCompleted) {
            return false;
        }

        sendConsentsToBloomreach(consentData.categories);
        return !!consentData.categories.advertisement;
    };

    const attachListeners = () => {
        if (listenersAttached) return;

        document.addEventListener('cookieyes_banner_load', checkConsent);
        document.addEventListener('cookieyes_consent_update', checkConsent);

        listenersAttached = true;
    };

    const loadCookieYes = async (): Promise<void> => {
        if (!import.meta.client) return;
        if (cookieYesLoaded) return; // Prevent duplicate injections
        if (cookieYesLoadingPromise) return cookieYesLoadingPromise; // Prevent parallel loads

        cookieYesLoadingPromise = new Promise((resolve, reject) => {
            const cookieYesId = config.public.cookieYesId;
            if (!cookieYesId) {
                reject(new Error('COOKIEYES_ID missing'));
                return;
            }

            const existingScript = document.getElementById('cookieyes');
            if (existingScript) {
                cookieYesLoaded = true;
                attachListeners(); // Attach event listeners once CookieYes is ready
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.id = 'cookieyes';
            script.type = 'text/javascript';
            script.src = `https://cdn-cookieyes.com/client_data/${cookieYesId}/script.js`;
            script.setAttribute('data-cy', `${cookieYesId}`); // TODO: remove template literal

            script.onload = () => {
                cookieYesLoaded = true;
                console.info('CookieYes ready');
                attachListeners(); // Attach event listeners once CookieYes is ready
                resolve();
            };

            script.onerror = () => {
                cookieYesLoadingPromise = null;
                reject(new Error('Failed loading CookieYes'));
            };

            document.head.appendChild(script);
        });

        return cookieYesLoadingPromise;
    };

    return { loadCookieYes, checkConsent };
};
